import type { AnalyticsTracker } from './AnalyticsTracker';

/**
 * CRM v2 Analytics Tracker (Functional)
 *
 * Sends events to CRM v2 backend (Supabase Edge Functions) for campaign automation.
 * Only tracks events after user login (setUser called).
 *
 * 🎯 Email 우선 정책:
 * - email이 확인된 이벤트만 distinctId를 email로 사용
 * - email이 없는 경우 userId 사용
 *
 * 🔑 인증 방식:
 * - X-Project-Token 헤더 사용 (CRM v2 전용)
 *
 * 🔄 Identify/Alias 처리:
 * - 서버 사이드에서 처리 (server-side-tracking Edge Function)
 * - 클라이언트에서는 track 이벤트만 전송
 */

// 모듈 스코프 상태 (singleton 대체)
let sharedDistinctId: string | null = null;
let sharedEmailVerified = false;

/**
 * 이메일 형식 검증 헬퍼
 */
function isValidEmail(email: string | null | undefined): email is string {
  if (!email || typeof email !== 'string') {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * properties에서 이메일 추출 ($email 우선, email 차선)
 */
function extractEmail(properties: Record<string, unknown>): string | null {
  const email = (properties?.$email || properties?.email) as string | null | undefined;
  return isValidEmail(email) ? email : null;
}

/**
 * properties에서 tiktok_sec_uid 추출
 * 'unavailable' 값은 무효한 것으로 처리 (backfill에서 마킹된 케이스)
 */
function extractSecUid(properties: Record<string, unknown>): string | null {
  const secUid = properties?.tiktok_sec_uid as string | null | undefined;
  if (!secUid || typeof secUid !== 'string' || secUid === 'unavailable') {
    return null;
  }
  return secUid;
}

/**
 * CRM v2 Tracker 팩토리 함수
 */
export function createCrmV2Tracker(supabaseUrl: string, projectToken: string): AnalyticsTracker {
  const baseUrl = `${supabaseUrl}/functions/v1`;

  return {
    /**
     * Store user info for subsequent event tracking
     * Note: Identify/Alias는 server-side-tracking에서 처리
     *
     * 🎯 sec_uid 우선 정책: tiktok_sec_uid → email → userId
     */
    setUser(userId: string, properties: Record<string, unknown>): void {
      const secUid = extractSecUid(properties);
      const email = extractEmail(properties);
      const finalDistinctId = secUid || email || userId;

      sharedEmailVerified = !!email;
      sharedDistinctId = finalDistinctId;
    },

    /**
     * Track event to CRM v2
     * Skips if user not identified (pre-login)
     */
    track(eventName: string, properties?: Record<string, unknown>): void {
      if (!sharedDistinctId) {
        return;
      }

      let finalDistinctId = sharedDistinctId;
      if (sharedEmailVerified && properties) {
        const email = extractEmail(properties);
        if (email) {
          finalDistinctId = email;
        }
      }

      fetch(`${baseUrl}/events-track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Project-Token': projectToken,
        },
        body: JSON.stringify({
          distinctId: finalDistinctId,
          event: eventName,
          properties: properties || {},
        }),
      }).catch(() => {
        // Track 실패 - 무시 (non-blocking)
      });
    },

    /**
     * Clear user identification on logout
     */
    clearUser(): void {
      sharedDistinctId = null;
      sharedEmailVerified = false;
    },
  };
}
